import { cn, formatCompactNumber, getTokenThresholdColor, getTokenThresholdBgColor, getTokenThresholdLabel } from '@/lib/utils'
import { Coins } from 'lucide-react'

export interface TokenBadgeProps {
  /** Total token count to display */
  tokens: number
  /** Whether to show "tokens" label */
  showLabel?: boolean
  /** Badge size variant */
  size?: 'sm' | 'md' | 'lg'
  /** Additional CSS classes */
  className?: string
}

const sizeClasses = {
  sm: 'px-1.5 py-0.5 text-xs gap-1',
  md: 'px-2 py-0.5 text-xs gap-1.5',
  lg: 'px-2.5 py-1 text-sm gap-2',
}

const iconSizes = {
  sm: 12,
  md: 14,
  lg: 16,
}

/**
 * TokenBadge displays a compact token count with color-coded threshold indication.
 * 
 * Color thresholds:
 * - Green (low): < 100,000 tokens
 * - Yellow (medium): 100,000 - 500,000 tokens
 * - Red (high): > 500,000 tokens
 */
export function TokenBadge({
  tokens,
  showLabel = true,
  size = 'md',
  className,
}: TokenBadgeProps) {
  const formattedTokens = formatCompactNumber(tokens)
  const colorClass = getTokenThresholdColor(tokens)
  const bgColorClass = getTokenThresholdBgColor(tokens)
  const thresholdLabel = getTokenThresholdLabel(tokens)

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md font-medium',
        sizeClasses[size],
        bgColorClass,
        colorClass,
        className
      )}
      title={`${tokens.toLocaleString()} tokens (${thresholdLabel})`}
      aria-label={`${tokens.toLocaleString()} tokens, ${thresholdLabel}`}
    >
      <Coins size={iconSizes[size]} aria-hidden="true" />
      <span>{formattedTokens}</span>
      {showLabel && <span className="opacity-75">tokens</span>}
    </span>
  )
}
